/* eslint-disable react/display-name */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import Image from '../image';
import LinkHandler from '../link-handler';
import { mimeTypeToString } from '../../utils/content-formatting';

import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  OrderedList,
  UnorderedList,
} from '../styled/typography';
import { Paragraph } from './styles';

import Quotation from '../quotation';

const SPAN = 'span';

/* The double breakline issue
 *
 * The problem: when copy pasting text into the contentful RTE from a text editor, sometimes
 * newlines are interpeted as breaklines and not appropriately converted to a new paragraph.
 * This can cause issues for assisted technology
 *
 * The solution:
 * - during rendertext, split the text whenever a double line break is encountered (linebreaks
 *   can be \n\r or \n) and insert a paragraph marker
 * - when rendering a paragraph, partition the `children` array whenever a paragraph marker is
 *   encountered and then render a paragraph for each partition
 *
 * The paragraph marker is an array with 2 linebreaks in it so that if the rendered text is
 * rendered as part of anything that _isn't_ a paragraph, it renders a double linebreak
 */

const PARAGRAPH_MARKER = [<br key="marker-1" />, <br key="marker-2" />];

// splits an array into multiple arrays, delimited by the delimitr
const splitArray = (array, delimiter) => {
  const result = [];
  let start = 0;
  let end = -1;
  // eslint-disable-next-line no-cond-assign
  while ((end = array.indexOf(delimiter, start)) !== -1) {
    result.push(array.slice(start, end));
    start = end + 1;
  }
  result.push(array.slice(start));

  return result.filter((item) => item.length > 0);
};

// split text whenever a single linebreak is encountered and insert a <br> tag

const splitBRs = (text) =>
  text
    .split(/(?:\r\n|\n)/)
    .reduce(
      (children, textSegment, index) =>
        index > 0
          ? [...children, <br key={`${index}-br`} />, textSegment]
          : [...children, textSegment],
      []
    )
    .filter((element) => Boolean(element));

const trimBRs = (array) => {
  while (array[0]?.type === 'br') {
    array.shift();
  }
  while (array[array.length - 1]?.type === 'br') {
    array.pop();
  }
  return array;
};

const processChildren = (children) => trimBRs(children.flat(Infinity));

// split text whenever a double linebreak is encountered and insert a paragraph marker
// the regex checks for either:
// - 2 or more \r\n, windows style return + newline
// - 2 or more \n, linux style return
// - 2 or more \r, old mac style return
// - 3 or more of \r or \n, to catch anything else

const splitParagraphs = (text) =>
  text?.length
    ? text
        .split(/(?:(?:\r\n){2,}|\n{2,}|\r{2,}|(?:\r|\n){3,})/)
        .reduce(
          (all, paragraph, index) =>
            index > 0
              ? [...all, PARAGRAPH_MARKER, ...splitBRs(paragraph)]
              : [...all, ...splitBRs(paragraph)],
          []
        )
    : [];

const renderHeading = (Heading, children) => {
  const processedChildren = processChildren(children);

  if (processedChildren?.length > 0) {
    return <Heading>{processedChildren}</Heading>;
  }

  return null;
};

const RenderNode = (linkData, RichTextAssembly, renderText = false) => {
  const renderNode = {
    renderText: (text) => splitParagraphs(text),
    renderMark: {
      [MARKS.BOLD]: (children) => <strong>{children}</strong>,
      [MARKS.ITALIC]: (children) => <em>{children}</em>,
      [SPAN]: (children) => (
        <>
          <span>{children}</span>{' '}
        </>
      ),
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => renderHeading(Heading1, children),
      [BLOCKS.HEADING_2]: (node, children) => renderHeading(Heading2, children),
      [BLOCKS.HEADING_3]: (node, children) => renderHeading(Heading3, children),
      [BLOCKS.HEADING_4]: (node, children) => renderHeading(Heading4, children),
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const partitionedChildren = splitArray(
          children.flat(1),
          PARAGRAPH_MARKER
        );

        const paragraphs = partitionedChildren.map((partition) => {
          const trimmedPartition = trimBRs(partition);
          if (trimmedPartition.length) {
            return <Paragraph>{trimmedPartition}</Paragraph>;
          }

          return null;
        });

        if (paragraphs.length > 1) {
          return paragraphs;
        }

        return paragraphs[0];
      },
      [BLOCKS.OL_LIST]: (node, children) => (
        <OrderedList>{children}</OrderedList>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <UnorderedList>{children}</UnorderedList>
      ),
      [BLOCKS.QUOTE]: (node, children) => <Quotation>{children}</Quotation>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        if (!node.data.target) return null;
        return <Image image={node.data.target} width={1000} addMarginBottom />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) =>
        node.data.target && RichTextAssembly ? (
          <RichTextAssembly assembly={node.data.target} />
        ) : null,
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        if (
          !node.data.target ||
          !node.data.target.file ||
          !node.data.target.file.url
        ) {
          return children;
        }

        const { target } = node.data;

        return (
          <LinkHandler
            link={{ ...target }}
            dataTracking={mimeTypeToString(target.file.contentType)}
          >
            {processChildren(children)}
          </LinkHandler>
        );
      },

      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        if (!node.data.target) return children;
        const { target } = node.data;

        const { internal, navigation } = target;

        if (internal?.type === 'ContentfulComponentExternalLink') {
          return (
            <LinkHandler
              link={{ external: true, ...node.data.target }}
              data={linkData}
            >
              {children}
            </LinkHandler>
          );
        }

        if (navigation && navigation.path) {
          return (
            <LinkHandler link={target} data={linkData}>
              {processChildren(children)}
            </LinkHandler>
          );
        }

        return children;
      },
      /* eslint-enable */
    },
  };

  /**
   * The renderText callback is a function that has a single string argument and
   * returns a React node. Each text node is evaluated individually by this callback.
   */
  if (renderText) {
    Object.assign(renderNode, {
      renderText: (text) => renderText(text),
    });
  }
  return renderNode;
};

export default RenderNode;
